// ============================================
// PAGE - Configuración
// ============================================

import { useState, useRef } from 'react';
import { goToPremium } from '@/lib/premium';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSucursales,
  useUsuarios,
  useEliminarSucursal,
  useEliminarUsuario,
  useUIStore,
  usePerfilAlmacen,
  useLogoAlmacen,
  useEliminarLogoAlmacen,
  useSubirLogoAlmacen,
  useSession,
  useAuthStore,
  useCategoriasAlmacen,
  useCreateCategoriaAlmacen,
  useUpdateCategoriaAlmacen,
  useDeleteCategoriaAlmacen,
} from '@almacenes/core';
import type { Sucursal, UsuarioSucursal, CategoriaAlmacen } from '@almacenes/core';
import { MdBusiness, MdPeople, MdAdd, MdEdit, MdDelete, MdStorefront, MdImage, MdCheck, MdCloudUpload, MdCategory, MdClose, MdPrint, MdCheckCircle, MdRefresh, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { FaCrown } from 'react-icons/fa';
import { CrearSucursalModal } from './components/CrearSucursalModal';
import { EditarSucursalModal } from './components/EditarSucursalModal';
import { CrearUsuarioModal } from './components/CrearUsuarioModal';
import { EditarUsuarioModal } from './components/EditarUsuarioModal';
import { ConfirmacionModal } from '../../components/ConfirmacionModal';
import { formatRutWithDv } from '../../hooks/useRutFormatter';
import { usePrintConfig, getFontScale, getScaledFontScale, printPx, printTicketHtml, PAPER_WIDTH_PX, PAPER_WIDTH_INFO, FONT_FAMILY_OPTIONS, PRINT_SCALE_OPTIONS } from '../../hooks/usePrintConfig';
import type { PaperWidth, PrintConfig } from '../../hooks/usePrintConfig';

const openTestPrint = (type: 'voucher' | 'boleta', config: PrintConfig) => {
  const s = getScaledFontScale(config);
  const px = (n: number) => printPx(config, n);

  const itemsHtml = `
    <tr><td style="text-align:center;padding:${px(1)}px;width:12%;font-size:${s.sm}px">2</td><td style="padding:${px(1)}px;width:56%;font-size:${s.sm}px">Pan marraqueta</td><td style="text-align:right;padding:${px(1)}px;width:32%;font-size:${s.sm}px">$600</td></tr>
    <tr><td style="text-align:center;padding:${px(1)}px;font-size:${s.sm}px">1</td><td style="padding:${px(1)}px;font-size:${s.sm}px">Leche entera 1L</td><td style="text-align:right;padding:${px(1)}px;font-size:${s.sm}px">$990</td></tr>
    <tr><td style="text-align:center;padding:${px(1)}px;font-size:${s.sm}px">3</td><td style="padding:${px(1)}px;font-size:${s.sm}px">Bebida 1.5L</td><td style="text-align:right;padding:${px(1)}px;font-size:${s.sm}px">$2.100</td></tr>`;

  const tableHeader = `
    <table style="width:100%;font-size:${s.sm}px;border-collapse:collapse;table-layout:fixed">
      <thead><tr style="border-bottom:1px dashed #000">
        <th style="text-align:center;width:18%;font-size:${s.sm}px;white-space:nowrap">Cant.</th>
        <th style="text-align:left;width:50%;font-size:${s.sm}px">Producto</th>
        <th style="text-align:right;width:32%;font-size:${s.sm}px;white-space:nowrap">Total</th>
      </tr></thead>
      <tbody>${itemsHtml}</tbody>
    </table>`;

  const hr = `<hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>`;

  let html: string;
  if (type === 'boleta') {
    html = `
      <div style="text-align:center;margin-bottom:${px(6)}px;font-size:${s.base}px">
        <div style="font-weight:bold">SOCIEDAD COMERCIAL LTDA.</div>
        <div>Venta al por menor de alimentos</div>
        <div style="font-weight:bold">Mi Almacén — Ticket de Prueba</div>
        <div>RUT: 12.345.678-9</div>
        <div>Los Aromos 123, Local 4</div>
        <div style="font-weight:bold;margin-top:${px(4)}px">BOLETA ELECTRÓNICA N° 99999</div>
        <div style="font-size:${s.sm}px">14/05/2026 10:32</div>
      </div>
      ${hr}${tableHeader}${hr}
      <table style="width:100%;font-size:${s.sm}px;table-layout:fixed">
        <tr><td colspan="2" style="padding:${px(1)}px">Monto Neto:</td><td style="text-align:right;padding:${px(1)}px">$3.101</td></tr>
        <tr><td colspan="2" style="padding:${px(1)}px">IVA (19%):</td><td style="text-align:right;padding:${px(1)}px">$589</td></tr>
        <tr style="font-weight:bold;font-size:${s.md}px"><td colspan="2" style="padding:${px(1)}px">TOTAL:</td><td style="text-align:right;padding:${px(1)}px">$3.690</td></tr>
      </table>
      ${hr}
      <div style="text-align:center;font-size:${s.sm}px">Verifique documento en www.sii.cl</div>
      ${hr}
      <div style="text-align:center;font-size:${s.sm}px">
        <div>Almacenes Digitales</div>
        <div style="font-weight:bold">¡Gracias por tu compra!</div>
        <div style="margin-top:${px(6)}px;font-size:${s.xs}px;font-style:italic">— TICKET DE PRUEBA —</div>
      </div>`;
  } else {
    html = `
      <div style="text-align:center;margin-bottom:${px(6)}px;font-size:${s.base}px">
        <div style="font-weight:bold;font-size:${s.title}px">VOUCHER N° 99999</div>
        <div>12.345.678-9</div>
        <div style="font-weight:bold">Mi Almacén — Ticket de Prueba</div>
        <div>Los Aromos 123, Local 4</div>
        <div style="font-size:${s.sm}px">14/05/2026 10:32</div>
      </div>
      ${hr}${tableHeader}${hr}
      <table style="width:100%;font-size:${s.sm}px;table-layout:fixed">
        <tr style="font-weight:bold;font-size:${s.md}px"><td colspan="2" style="padding:${px(1)}px">TOTAL:</td><td style="text-align:right;padding:${px(1)}px">$3.690</td></tr>
      </table>
      ${hr}
      <div style="text-align:center;font-size:${s.xs}px;font-style:italic">*Voucher no válido como boleta electrónica*</div>
      ${hr}
      <div style="text-align:center;font-size:${s.sm}px">
        <div>Almacenes Digitales</div>
        <div style="font-weight:bold">¡Gracias por tu compra!</div>
        <div style="margin-top:${px(6)}px;font-size:${s.xs}px;font-style:italic">— TICKET DE PRUEBA —</div>
      </div>`;
  }

  return printTicketHtml(html, config);
};


type TabType = 'perfil' | 'sucursales' | 'usuarios' | 'impresion' | 'logo' | 'categorias';

export const ConfiguracionPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>('perfil');
  const [isCrearSucursalModalOpen, setIsCrearSucursalModalOpen] = useState(false);
  const [isCrearUsuarioModalOpen, setIsCrearUsuarioModalOpen] = useState(false);
  const [sucursalToEdit, setSucursalToEdit] = useState<Sucursal | null>(null);
  const { draft: printDraft, updateDraft, save: savePrint, discard: discardPrint, reset: resetPrint, isDirty: isPrintDirty } = usePrintConfig();
  const [previewType, setPreviewType] = useState<'voucher' | 'boleta'>('voucher');
  const [isTestPrinting, setIsTestPrinting] = useState(false);
  const [sucursalToDelete, setSucursalToDelete] = useState<Sucursal | null>(null);
  const [usuarioToEdit, setUsuarioToEdit] = useState<UsuarioSucursal | null>(null);
  const [usuarioToDelete, setUsuarioToDelete] = useState<UsuarioSucursal | null>(null);
  const [showEliminarLogoModal, setShowEliminarLogoModal] = useState(false);
  const [showSubirLogoModal, setShowSubirLogoModal] = useState(false);
  const [isChangingLogo, setIsChangingLogo] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [colorFondo, setColorFondo] = useState('#FFFFFF');
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const { data: perfilAlmacen, isLoading: isLoadingPerfil, error: errorPerfil } = usePerfilAlmacen();
  const { data: session } = useSession();
  const { data: sucursales, isLoading: isLoadingSucursales, error: errorSucursales } = useSucursales();
  const { data: usuarios, isLoading: isLoadingUsuarios, error: errorUsuarios } = useUsuarios();
  const { data: logoAlmacen, isLoading: isLoadingLogo } = useLogoAlmacen(activeTab === 'logo' && !!session?.hasPremiumAccess);
  const eliminarSucursalMutation = useEliminarSucursal();
  const eliminarUsuarioMutation = useEliminarUsuario();
  const eliminarLogoMutation = useEliminarLogoAlmacen();
  const subirLogoMutation = useSubirLogoAlmacen();
  const addToast = useUIStore((state) => state.addToast);

  const hasPremiumAccess = session?.hasPremiumAccess || false;

  // --- Categorías ---
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const [categoriaEditando, setCategoriaEditando] = useState<CategoriaAlmacen | null>(null);
  const [nombreEditando, setNombreEditando] = useState('');
  const [categoriaAEliminar, setCategoriaAEliminar] = useState<CategoriaAlmacen | null>(null);

  const { data: categorias, isLoading: isLoadingCategorias } = useCategoriasAlmacen();
  const createCategoria = useCreateCategoriaAlmacen();
  const updateCategoria = useUpdateCategoriaAlmacen();
  const deleteCategoria = useDeleteCategoriaAlmacen();

  const ordenKey = session?.almacen?.id ? `cat_order_${session.almacen.id}` : null;

  const getOrdenGuardado = (): number[] => {
    if (!ordenKey) return [];
    try { return JSON.parse(localStorage.getItem(ordenKey) || '[]'); } catch { return []; }
  };

  const categoriasOrdenadas = (() => {
    if (!categorias) return [];
    const ordenGuardado = getOrdenGuardado();
    if (ordenGuardado.length === 0) return categorias;
    const mapa = new Map(categorias.map((c) => [c.id, c]));
    const ordenadas = ordenGuardado.flatMap((id) => (mapa.has(id) ? [mapa.get(id)!] : []));
    const restantes = categorias.filter((c) => !ordenGuardado.includes(c.id));
    return [...ordenadas, ...restantes];
  })();

  const moverCategoria = (index: number, direccion: 'arriba' | 'abajo') => {
    const lista = [...categoriasOrdenadas];
    const swapIdx = direccion === 'arriba' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= lista.length) return;
    [lista[index], lista[swapIdx]] = [lista[swapIdx], lista[index]];
    if (ordenKey) localStorage.setItem(ordenKey, JSON.stringify(lista.map((c) => c.id)));
  };

  const handleCrearCategoria = () => {
    const nombre = nuevaCategoria.trim();
    if (!nombre) return;
    if (nombre.length > 50) {
      addToast('error', 'El nombre no puede superar los 50 caracteres.');
      return;
    }
    createCategoria.mutate(nombre, {
      onSuccess: () => {
        setNuevaCategoria('');
        addToast('success', 'Categoría creada');
      },
      onError: (err: any) => {
        const msg = err?.statusCode === 409 ? 'Ya existe una categoría con ese nombre.' : 'Error al crear la categoría.';
        addToast('error', msg);
      },
    });
  };

  const handleGuardarEdicion = () => {
    const nombre = nombreEditando.trim();
    if (!nombre || !categoriaEditando) return;
    if (nombre.length > 50) {
      addToast('error', 'El nombre no puede superar los 50 caracteres.');
      return;
    }
    updateCategoria.mutate(
      { id: categoriaEditando.id, nombre },
      {
        onSuccess: () => {
          setCategoriaEditando(null);
          addToast('success', 'Categoría actualizada');
        },
        onError: (err: any) => {
          const msg = err?.statusCode === 409 ? 'Ya existe una categoría con ese nombre.' : 'Error al actualizar.';
          addToast('error', msg);
        },
      }
    );
  };

  const handleConfirmarEliminar = () => {
    if (!categoriaAEliminar) return;
    deleteCategoria.mutate(categoriaAEliminar.id, {
      onSuccess: () => {
        setCategoriaAEliminar(null);
        addToast('success', 'Categoría eliminada');
      },
      onError: () => addToast('error', 'Error al eliminar la categoría.'),
    });
  };

  const formatFecha = (fecha: string) => {
    const date = new Date(fecha);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatRut = (rut: string | number, dv: string) => {
    return `${rut.toString()}-${dv}`;
  };

  const handleEliminarSucursal = async () => {
    if (!sucursalToDelete) return;

    try {
      await eliminarSucursalMutation.mutateAsync(sucursalToDelete.id);
      addToast('success', 'Sucursal eliminada correctamente');
      setSucursalToDelete(null);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al eliminar la sucursal';
      addToast('error', errorMessage);
      setSucursalToDelete(null);
    }
  };

  const handleEliminarUsuario = async () => {
    if (!usuarioToDelete) return;

    try {
      await eliminarUsuarioMutation.mutateAsync(usuarioToDelete.id);
      addToast('success', 'Usuario eliminado correctamente');
      setUsuarioToDelete(null);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al eliminar el usuario';
      addToast('error', errorMessage);
      setUsuarioToDelete(null);
    }
  };

  const handleEliminarLogo = async () => {
    try {
      await eliminarLogoMutation.mutateAsync();
      
      // Actualizar la sesión eliminando el logo y color de fondo
      const updateAlmacenLogo = useAuthStore.getState().updateAlmacenLogo;
      updateAlmacenLogo(undefined, undefined);
      
      // Actualizar el cache de React Query directamente sin hacer refetch
      queryClient.setQueryData(['almacenes', 'logo'], null);
      
      addToast('success', 'Logo eliminado correctamente');
      setShowEliminarLogoModal(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al eliminar el logo';
      addToast('error', errorMessage);
      setShowEliminarLogoModal(false);
    }
  };

  const validateFile = (file: File): string | null => {
    // Validar extensión
    const validExtensions = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validExtensions.includes(file.type)) {
      return 'Solo se permiten archivos PNG o JPG';
    }

    // Validar tamaño (máximo 200 KB)
    const maxSize = 200 * 1024; // 200 KB en bytes
    if (file.size > maxSize) {
      return 'El archivo no puede superar los 200 KB';
    }

    return null;
  };

  const validateImageDimensions = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        
        // Validar dimensiones mínimas
        if (img.width < 128 || img.height < 128) {
          resolve('Las dimensiones mínimas son 128×128 px');
          return;
        }

        // Validar dimensiones máximas
        if (img.width > 512 || img.height > 512) {
          resolve('Las dimensiones máximas son 512×512 px');
          return;
        }

        resolve(null);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve('Error al cargar la imagen');
      };

      img.src = url;
    });
  };

  const handleFileSelect = async (file: File) => {
    setValidationError(null);

    // Validar archivo
    const fileError = validateFile(file);
    if (fileError) {
      setValidationError(fileError);
      return;
    }

    // Validar dimensiones
    const dimensionError = await validateImageDimensions(file);
    if (dimensionError) {
      setValidationError(dimensionError);
      return;
    }

    // Si todo está bien, guardar el archivo y mostrar preview
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleSubirLogo = async () => {
    if (!selectedFile) return;

    try {
      const response = await subirLogoMutation.mutateAsync({ file: selectedFile, colorFondo });
      
      // Actualizar la sesión con el nuevo logo y color de fondo
      const updateAlmacenLogo = useAuthStore.getState().updateAlmacenLogo;
      updateAlmacenLogo(response.url, response.colorFondo);
      
      // Actualizar el cache de React Query con los datos de la respuesta (sin hacer refetch)
      queryClient.setQueryData(['almacenes', 'logo'], response);
      
      addToast('success', 'Logo subido correctamente');
      setShowSubirLogoModal(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setColorFondo('#FFFFFF');
      setValidationError(null);
      setIsChangingLogo(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al subir el logo';
      addToast('error', errorMessage);
      setShowSubirLogoModal(false);
    }
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setColorFondo('#FFFFFF');
    setValidationError(null);
    setIsChangingLogo(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const menuItems = [
    { id: 'perfil' as TabType, label: 'Perfil del negocio', icon: MdStorefront, locked: false },
    { id: 'sucursales' as TabType, label: 'Sucursales', icon: MdBusiness, locked: false },
    { id: 'usuarios' as TabType, label: 'Usuarios', icon: MdPeople, locked: false },
    { id: 'impresion' as TabType, label: 'Impresión', icon: MdPrint, locked: false },
    { id: 'logo' as TabType, label: 'Logo', icon: MdImage, locked: false, isPremium: true },
    { id: 'categorias' as TabType, label: 'Categorías', icon: MdCategory, locked: false, isPremium: true },
  ];

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
          Configuración
        </h1>
        <p className="text-gray-600 mt-1">
          Administra la información de tu negocio
        </p>
      </div>

      {/* Layout Principal: Menú Lateral + Contenido */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Menú Lateral */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <nav className="flex flex-col">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                const isPremiumFeature = (item as any).isPremium || false;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center gap-3 px-6 py-4 text-left transition-all relative ${
                      isActive
                        ? 'bg-[#43BFFF] bg-opacity-10 border-l-4 border-[#43BFFF] text-[#43BFFF] font-semibold'
                        : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                  >
                    <Icon className="text-2xl flex-shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {isPremiumFeature && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                        <FaCrown className="text-xs text-white" />
                        <span className="text-xs font-bold text-white">Premium</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Contenido Principal */}
        <div className="lg:col-span-3">
          {/* Perfil del Negocio */}
          {activeTab === 'perfil' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                <div className="p-3 rounded-xl bg-[#43BFFF] bg-opacity-10">
                  <MdStorefront className="text-3xl" style={{ color: '#43BFFF' }} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                    Perfil del negocio
                  </h2>
                  <p className="text-sm text-gray-600">
                    Información general de tu almacén
                  </p>
                </div>
              </div>

              {isLoadingPerfil ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                  <p className="mt-4 text-gray-600">Cargando información...</p>
                </div>
              ) : errorPerfil ? (
                <div className="text-center py-12 text-red-500">
                  Error al cargar la información del negocio. Por favor, intente nuevamente.
                </div>
              ) : perfilAlmacen ? (
                <div className="space-y-6">
                  {/* Información Básica */}
                  <div>
                    <h3 className="text-lg font-bold mb-4" style={{ color: '#11194F' }}>
                      Información Básica
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">Nombre del Almacén</p>
                        <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                          {perfilAlmacen.nombre}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">RUT</p>
                        <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                          {formatRutWithDv(perfilAlmacen.rut)}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">Dirección</p>
                        <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                          {perfilAlmacen.direccion} #{perfilAlmacen.numeroDireccion}
                          {perfilAlmacen.local && `, Local ${perfilAlmacen.local}`}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">Comuna</p>
                        <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                          {perfilAlmacen.comuna.nombre}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">Región</p>
                        <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                          {perfilAlmacen.comuna.region.nombre}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">Fecha de Creación</p>
                        <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                          {formatFecha(perfilAlmacen.fechaCreacion)}
                        </p>
                      </div>                      
                    </div>
                  </div>

                  {/* Características del Local */}
                  <div>
                    <h3 className="text-lg font-bold mb-4" style={{ color: '#11194F' }}>
                      Características del Local
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">Tamaño del Local</p>
                        <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                          {perfilAlmacen.tamanoLocal || 'No especificado'}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-1">Tipo de Almacén</p>
                        <p className="flex items-center gap-1 text-base font-semibold" style={{ color: '#11194F' }}>
                          {perfilAlmacen.tipoAlmacen || 'No especificado'}
                          {perfilAlmacen.tipoAlmacenValido && (
                            <MdCheck
                              className="text-green-500 text-base"
                              title="Tipo de almacén válido"
                              aria-label="Tipo de almacén válido"
                            />
                          )}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-2">Servicios y Características</p>
                        <div className="flex flex-wrap gap-2">
                          {perfilAlmacen.tienePatenteAlcohol && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              Patente de Alcohol
                            </span>
                          )}
                          {perfilAlmacen.tieneFrios && (
                            <span className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-xs font-medium">
                              Productos Fríos
                            </span>
                          )}
                          {perfilAlmacen.tieneCongelados && (
                            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                              Productos Congelados
                            </span>
                          )}
                          {perfilAlmacen.vendePorMayor && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                              Venta por Mayor
                            </span>
                          )}
                          {!perfilAlmacen.tienePatenteAlcohol && 
                           !perfilAlmacen.tieneFrios && 
                           !perfilAlmacen.tieneCongelados && 
                           !perfilAlmacen.vendePorMayor && (
                            <span className="text-sm text-gray-500">
                              Sin características especiales
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Contenido de Sucursales */}
          {activeTab === 'sucursales' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* Header con botón agregar */}
              <div className="mb-6 flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-[#43BFFF] bg-opacity-10">
                    <MdBusiness className="text-3xl" style={{ color: '#43BFFF' }} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                      Sucursales
                    </h2>
                    <p className="text-sm text-gray-600">
                      Administra las sucursales de tu negocio
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsCrearSucursalModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#43BFFF] text-white rounded-lg font-medium hover:bg-[#3AABEB] transition-colors"
                >
                  <MdAdd className="text-xl" />
                  Nueva Sucursal
                </button>
              </div>

              {/* Tabla de Sucursales */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ backgroundColor: '#F3F4F9' }}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        Nombre
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        Dirección
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        Comuna
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        RUT
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        Fecha Creación
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                        Estado
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingSucursales ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                          </div>
                          <p className="mt-4 text-gray-600">Cargando sucursales...</p>
                        </td>
                      </tr>
                    ) : errorSucursales ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-red-500">
                          Error al cargar las sucursales. Por favor, intente nuevamente.
                        </td>
                      </tr>
                    ) : !sucursales || sucursales.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-gray-600">
                          No hay sucursales registradas.
                        </td>
                      </tr>
                    ) : (
                      sucursales.map((sucursal: Sucursal, index) => (
                        <tr
                          key={sucursal.id}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="px-6 py-4">
                            <span className="font-semibold" style={{ color: '#11194F' }}>
                              {sucursal.nombre}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {sucursal.direccion} #{sucursal.numeroDireccion}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">{sucursal.comuna}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {formatRut(sucursal.rut, sucursal.dv)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {formatFecha(sucursal.fechaCreacion)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {sucursal.activo ? (
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                Activa
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                                Inactiva
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => setSucursalToEdit(sucursal)}
                                className="p-2 rounded-lg transition-all hover:scale-110"
                                style={{ color: '#43BFFF' }}
                                title="Editar"
                              >
                                <MdEdit className="text-xl" />
                              </button>
                              <button
                                onClick={() => setSucursalToDelete(sucursal)}
                                className="p-2 rounded-lg transition-all hover:scale-110"
                                style={{ color: '#EF4444' }}
                                title="Eliminar"
                              >
                                <MdDelete className="text-xl" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Info */}
              {sucursales && sucursales.length > 0 && (
                <div className="mt-4 text-sm text-gray-600">
                  Mostrando {sucursales.length} sucursal{sucursales.length !== 1 ? 'es' : ''}
                </div>
              )}
            </div>
          )}

          {/* Contenido de Usuarios */}
          {activeTab === 'usuarios' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* Header con botón agregar */}
              <div className="mb-6 flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-[#43BFFF] bg-opacity-10">
                    <MdPeople className="text-3xl" style={{ color: '#43BFFF' }} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                      Usuarios
                    </h2>
                    <p className="text-sm text-gray-600">
                      Administra los usuarios del sistema
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsCrearUsuarioModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#43BFFF] text-white rounded-lg font-medium hover:bg-[#3AABEB] transition-colors"
                >
                  <MdAdd className="text-xl" />
                  Nuevo Usuario
                </button>
              </div>

              {/* Tabla de Usuarios */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ backgroundColor: '#F3F4F9' }}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        Nombre
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        Apellidos
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        RUN
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                        Correo
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                        Rol
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                        Estado
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingUsuarios ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                          </div>
                          <p className="mt-4 text-gray-600">Cargando usuarios...</p>
                        </td>
                      </tr>
                    ) : errorUsuarios ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-red-500">
                          Error al cargar los usuarios. Por favor, intente nuevamente.
                        </td>
                      </tr>
                    ) : !usuarios || usuarios.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-gray-600">
                          No hay usuarios registrados.
                        </td>
                      </tr>
                    ) : (
                      usuarios.map((usuario: UsuarioSucursal, index) => (
                        <tr
                          key={usuario.id}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="px-6 py-4">
                            <span className="font-semibold" style={{ color: '#11194F' }}>
                              {usuario.nombre}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {usuario.apellidos || '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {usuario.run && usuario.dv ? formatRut(usuario.run.toString(), usuario.dv) : '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {usuario.correo || '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {usuario.esPropietario ? (
                              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                Propietario
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                Vendedor
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            {usuario.activo ? (
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                Activo
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                                Inactivo
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => usuario.activo && setUsuarioToEdit(usuario)}
                                disabled={!usuario.activo}
                                className={`p-2 rounded-lg transition-all ${
                                  usuario.activo ? 'hover:scale-110 cursor-pointer' : 'cursor-not-allowed opacity-40'
                                }`}
                                style={{ color: '#43BFFF' }}
                                title={usuario.activo ? 'Editar' : 'No disponible para usuarios inactivos'}
                              >
                                <MdEdit className="text-xl" />
                              </button>
                              <button
                                onClick={() => {
                                  if (usuario.activo && !usuario.esPropietario) {
                                    setUsuarioToDelete(usuario);
                                  }
                                }}
                                disabled={!usuario.activo || usuario.esPropietario}
                                className={`p-2 rounded-lg transition-all ${
                                  usuario.activo && !usuario.esPropietario
                                    ? 'hover:scale-110 cursor-pointer'
                                    : 'cursor-not-allowed opacity-40'
                                }`}
                                style={{ color: '#EF4444' }}
                                title={
                                  !usuario.activo
                                    ? 'No disponible para usuarios inactivos'
                                    : usuario.esPropietario
                                    ? 'No se puede eliminar un propietario'
                                    : 'Eliminar'
                                }
                              >
                                <MdDelete className="text-xl" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Info */}
              {usuarios && usuarios.length > 0 && (
                <div className="mt-4 text-sm text-gray-600">
                  Mostrando {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          {/* Contenido de Impresión */}
          {activeTab === 'impresion' && (() => {
            const s = getFontScale(printDraft.fontSize);
            const hr = <hr style={{ border: 'none', borderTop: '1px dashed #000', margin: '4px 0' }} />;
            const itemsPreview = (
              <tbody>
                <tr><td style={{ width: '18%', textAlign: 'center', padding: '1px' }}>2</td><td style={{ width: '50%', padding: '1px' }}>Pan marraqueta</td><td style={{ width: '32%', textAlign: 'right', padding: '1px', whiteSpace: 'nowrap' }}>$600</td></tr>
                <tr><td style={{ textAlign: 'center', padding: '1px' }}>1</td><td style={{ padding: '1px' }}>Leche entera 1L</td><td style={{ textAlign: 'right', padding: '1px', whiteSpace: 'nowrap' }}>$990</td></tr>
                <tr><td style={{ textAlign: 'center', padding: '1px' }}>3</td><td style={{ padding: '1px' }}>Bebida 1.5L</td><td style={{ textAlign: 'right', padding: '1px', whiteSpace: 'nowrap' }}>$2.100</td></tr>
              </tbody>
            );
            const tableHead = (
              <thead>
                <tr style={{ borderBottom: '1px dashed #000' }}>
                  <th style={{ width: '18%', textAlign: 'center', fontSize: `${s.sm}px`, whiteSpace: 'nowrap' }}>Cant.</th>
                  <th style={{ width: '50%', textAlign: 'left', fontSize: `${s.sm}px` }}>Producto</th>
                  <th style={{ width: '32%', textAlign: 'right', fontSize: `${s.sm}px`, whiteSpace: 'nowrap' }}>Total</th>
                </tr>
              </thead>
            );

            const ticketContent = previewType === 'voucher' ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: `${s.title}px` }}>VOUCHER N° 456</div>
                  <div>12.345.678-9</div>
                  <div style={{ fontWeight: 'bold' }}>Mi Almacén</div>
                  <div>Los Aromos 123, Local 4</div>
                  <div style={{ fontSize: `${s.sm}px` }}>14/05/2026 10:32</div>
                </div>
                {hr}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: `${s.sm}px`, tableLayout: 'fixed' }}>
                  {tableHead}{itemsPreview}
                </table>
                {hr}
                <table style={{ width: '100%', fontSize: `${s.sm}px`, tableLayout: 'fixed' }}>
                  <tbody>
                    <tr style={{ fontWeight: 'bold', fontSize: `${s.md}px` }}>
                      <td colSpan={2} style={{ padding: '1px' }}>TOTAL:</td>
                      <td style={{ textAlign: 'right', padding: '1px', whiteSpace: 'nowrap' }}>$3.690</td>
                    </tr>
                  </tbody>
                </table>
                {hr}
                <div style={{ textAlign: 'center', fontSize: `${s.xs}px`, fontStyle: 'italic' }}>*Voucher no válido como boleta electrónica*</div>
                {hr}
                <div style={{ textAlign: 'center', fontSize: `${s.sm}px` }}>
                  <div>Almacenes Digitales</div>
                  <div style={{ fontWeight: 'bold' }}>¡Gracias por tu compra!</div>
                </div>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: '4px', fontSize: `${s.base}px` }}>
                  <div style={{ fontWeight: 'bold' }}>SOCIEDAD COMERCIAL LTDA.</div>
                  <div>Venta al por menor de alimentos</div>
                  <div style={{ fontWeight: 'bold' }}>Mi Almacén</div>
                  <div>RUT: 12.345.678-9</div>
                  <div>Los Aromos 123, Local 4</div>
                  <div style={{ fontWeight: 'bold', marginTop: '4px' }}>BOLETA ELECTRÓNICA N° 98765</div>
                  <div style={{ fontSize: `${s.sm}px` }}>14/05/2026 10:32</div>
                </div>
                {hr}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: `${s.sm}px`, tableLayout: 'fixed' }}>
                  {tableHead}{itemsPreview}
                </table>
                {hr}
                <table style={{ width: '100%', fontSize: `${s.sm}px`, tableLayout: 'fixed' }}>
                  <tbody>
                    <tr><td colSpan={2} style={{ padding: '1px' }}>Monto Neto:</td><td style={{ textAlign: 'right', padding: '1px', whiteSpace: 'nowrap' }}>$3.101</td></tr>
                    <tr><td colSpan={2} style={{ padding: '1px' }}>IVA (19%):</td><td style={{ textAlign: 'right', padding: '1px', whiteSpace: 'nowrap' }}>$589</td></tr>
                    <tr style={{ fontWeight: 'bold', fontSize: `${s.md}px` }}>
                      <td colSpan={2} style={{ padding: '1px' }}>TOTAL:</td>
                      <td style={{ textAlign: 'right', padding: '1px', whiteSpace: 'nowrap' }}>$3.690</td>
                    </tr>
                  </tbody>
                </table>
                {hr}
                <div style={{ textAlign: 'center', margin: '4px 0' }}>
                  <div style={{ width: '100%', height: '28px', background: 'repeating-linear-gradient(90deg,#555 0,#555 1px,#fff 1px,#fff 3px)', borderRadius: '2px' }} />
                  <div style={{ fontSize: `${s.xs}px`, marginTop: '2px', color: '#888' }}>Código PDF417 (SII)</div>
                </div>
                <div style={{ textAlign: 'center', fontSize: `${s.sm}px` }}>Verifique documento en www.sii.cl</div>
                {hr}
                <div style={{ textAlign: 'center', fontSize: `${s.sm}px` }}>
                  <div>Almacenes Digitales</div>
                  <div style={{ fontWeight: 'bold' }}>¡Gracias por tu compra!</div>
                </div>
              </>
            );

            return (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Panel de configuración */}
                <div className="lg:col-span-7">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                      <h3 className="text-base font-bold text-[#11194F]">Ajustes de Impresión</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Configura cómo se verán tus tickets físicos</p>
                      <p className="text-xs text-gray-400 mt-2">
                        💡 Se guarda en este computador. Si cambias de equipo o borras los datos del navegador, deberás configurar nuevamente.
                      </p>
                    </div>

                    <div className="divide-y divide-gray-50">

                      {/* Ancho del papel */}
                      <div className="px-5 py-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
                          Ancho del rollo térmico
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {(['58mm', '80mm', '110mm'] as PaperWidth[]).map((w) => (
                            <button
                              key={w}
                              onClick={() => updateDraft({ paperWidth: w })}
                              className={`relative p-3 rounded-xl border-2 text-left transition-all ${
                                printDraft.paperWidth === w
                                  ? 'border-[#43BFFF] bg-blue-50'
                                  : 'border-gray-100 hover:border-gray-200'
                              }`}
                            >
                              <div className={`font-bold text-sm ${printDraft.paperWidth === w ? 'text-[#11194F]' : 'text-gray-500'}`}>{w}</div>
                              <div className="text-[10px] text-gray-400 mt-0.5 leading-snug">{PAPER_WIDTH_INFO[w].desc}</div>
                              {printDraft.paperWidth === w && (
                                <MdCheckCircle className="absolute top-2 right-2 text-[#43BFFF] text-sm" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tamaño de letra */}
                      <div className="px-5 py-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
                          Tamaño de letra
                        </label>
                        <div className="flex gap-2">
                          {([8, 9, 10, 12, 14, 16] as const).map((size) => (
                            <button
                              key={size}
                              onClick={() => updateDraft({ fontSize: size })}
                              className={`flex-1 py-2.5 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                                printDraft.fontSize === size
                                  ? 'border-[#43BFFF] bg-blue-50 text-[#11194F]'
                                  : 'border-gray-100 text-gray-400 hover:border-gray-200'
                              }`}
                            >
                              <span style={{ fontSize: `${Math.min(size, 14)}px`, lineHeight: 1, fontFamily: printDraft.fontFamily }} className="font-bold">A</span>
                              <span className="text-[9px]">{size}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tipo de letra */}
                      <div className="px-5 py-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
                          Tipo de letra
                        </label>
                        <select
                          value={printDraft.fontFamily}
                          onChange={(e) => updateDraft({ fontFamily: e.target.value as typeof printDraft.fontFamily })}
                          className="w-full border-2 border-gray-100 rounded-xl px-4 py-3 text-sm text-[#11194F] font-medium bg-white focus:outline-none focus:border-[#43BFFF] transition-colors cursor-pointer"
                        >
                          {FONT_FAMILY_OPTIONS.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <div className="mt-2 px-1 text-sm text-gray-400" style={{ fontFamily: printDraft.fontFamily }}>
                          Total $3.690 — Pan marraqueta
                        </div>
                      </div>

                      {/* Escala de impresión (calibración) */}
                      <div className="px-5 py-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
                          Escala de impresión
                        </label>
                        <p className="text-[11px] text-gray-400 mb-3">
                          Algunas impresoras térmicas (según el driver instalado en este equipo) achican o agrandan el ticket respecto al ancho real del rollo.
                          Si el texto se ve más pequeño de lo esperado, sube este valor; si se corta o se ve más grande de lo esperado, bájalo.
                          Esta calibración es independiente para cada equipo/impresora y no afecta la vista previa web.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {PRINT_SCALE_OPTIONS.map((scale) => (
                            <button
                              key={scale}
                              onClick={() => updateDraft({ printScale: scale })}
                              className={`px-3 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                                printDraft.printScale === scale
                                  ? 'border-[#43BFFF] bg-blue-50 text-[#11194F]'
                                  : 'border-gray-100 text-gray-400 hover:border-gray-200'
                              }`}
                            >
                              {scale}%
                            </button>
                          ))}
                        </div>
                      </div>

                    </div>

                    <div className="px-5 py-4 bg-gray-50/80 border-t border-gray-100 flex flex-wrap items-center gap-3">
                      <button
                        onClick={savePrint}
                        disabled={!isPrintDirty}
                        className={`flex-1 min-w-[120px] py-3 rounded-xl font-bold transition-all ${
                          isPrintDirty
                            ? 'bg-[#11194F] text-white hover:bg-[#1a2570] active:scale-95'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Guardar
                      </button>
                      {isPrintDirty && (
                        <button
                          onClick={discardPrint}
                          className="py-3 px-4 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:text-red-500 hover:border-red-200 transition-colors"
                        >
                          Descartar
                        </button>
                      )}
                      <button
                        onClick={resetPrint}
                        className="flex items-center gap-1.5 py-3 px-4 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <MdRefresh className="text-base" />
                        Predeterminado
                      </button>
                    </div>
                  </div>
                </div>

                {/* Vista previa */}
                <div className="lg:col-span-5">
                  <div className="sticky top-8 space-y-4">
                    <div className="bg-[#1e2330] rounded-[2.5rem] p-8 shadow-2xl flex flex-col items-center">
                      <div className="flex bg-[#2d3548] p-1.5 rounded-2xl mb-6 w-full max-w-[240px]">
                        {(['voucher', 'boleta'] as const).map((type) => (
                          <button
                            key={type}
                            onClick={() => setPreviewType(type)}
                            className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                              previewType === type
                                ? 'bg-[#43BFFF] text-white shadow-lg'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>

                      <div
                        className="bg-white shadow-2xl p-3 overflow-auto max-h-[500px]"
                        style={{
                          fontFamily: printDraft.fontFamily,
                          fontSize: `${s.base}px`,
                          width: `${PAPER_WIDTH_PX[printDraft.paperWidth]}px`,
                          color: '#000',
                        }}
                      >
                        {ticketContent}
                      </div>

                      <button
                        onClick={async () => {
                          setIsTestPrinting(true);
                          try {
                            await openTestPrint(previewType, printDraft);
                          } finally {
                            setIsTestPrinting(false);
                          }
                        }}
                        disabled={isTestPrinting}
                        className="mt-6 w-full flex items-center justify-center gap-3 py-4 rounded-2xl border-2 border-[#43BFFF] text-[#43BFFF] font-bold hover:bg-[#43BFFF] hover:text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isTestPrinting ? (
                          <>
                            <span className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-solid border-current border-r-transparent" />
                            Imprimiendo...
                          </>
                        ) : (
                          <>
                            <MdPrint className="text-xl" />
                            Imprimir Prueba Real
                          </>
                        )}
                      </button>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                      <span className="shrink-0 w-6 h-6 flex items-center justify-center bg-white rounded-lg text-[#43BFFF] shadow-sm text-xs font-bold">!</span>
                      <p className="text-[11px] text-blue-800 leading-relaxed">
                        En el diálogo del navegador quita los <b>márgenes</b> y ajusta el <b>tamaño de papel</b> ({printDraft.paperWidth}) para que coincida con tu impresora.{' '}
                        La vista previa es solo referencial — el resultado final puede variar según el modelo y configuración de tu impresora.
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            );
          })()}

          {/* Contenido de Logo */}
          {activeTab === 'logo' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                  <MdImage className="text-3xl text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                    Logo del Negocio
                  </h2>
                  <p className="text-sm text-gray-600">
                    {hasPremiumAccess ? 'Personaliza el logo de tu almacén' : 'Función exclusiva Premium'}
                  </p>
                </div>
              </div>

              {!hasPremiumAccess ? (
                // Sin Premium - Mostrar pantalla bloqueada
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 mb-6">
                    <FaCrown className="text-5xl text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#11194F' }}>
                    Función Premium
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    La personalización del logo está disponible solo para usuarios con suscripción Premium.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => goToPremium()}
                      className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                    >
                      Ver Planes
                    </button>
                  </div>
                </div>
              ) : isLoadingLogo ? (
                // Cargando logo
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                  <p className="mt-4 text-gray-600">Cargando logo...</p>
                </div>
              ) : logoAlmacen && !isChangingLogo ? (
                // Mostrar logo
                <div className="space-y-6">
                  {/* Información del Logo */}
                  <div>
                    <h3 className="text-lg font-bold mb-4" style={{ color: '#11194F' }}>
                      Logo Actual
                    </h3>
                    <div className="flex flex-col items-center">
                      {/* Contenedor con color de fondo */}
                      <div 
                        className="relative rounded-full shadow-lg mb-6 flex items-center justify-center"
                        style={{ 
                          backgroundColor: logoAlmacen.colorFondo,
                          border: '3px solid rgb(17, 25, 79)',
                          width: '300px',
                          height: '300px',
                          padding: '40px'
                        }}
                      >
                        <img
                          src={logoAlmacen.url}
                          alt="Logo del almacén"
                          className="object-contain w-full h-full"
                          style={{
                            borderRadius: '15px'
                          }}
                        />
                      </div>
                      
                      {/* Detalles del Logo */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        <div className="p-4 rounded-lg bg-gray-50">
                          <p className="text-sm text-gray-600 mb-1">Dimensiones</p>
                          <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                            {logoAlmacen.ancho} × {logoAlmacen.alto} px
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-gray-50">
                          <p className="text-sm text-gray-600 mb-1">Tamaño</p>
                          <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                            {(logoAlmacen.tamanoBytes / 1024).toFixed(2)} KB
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-gray-50">
                          <p className="text-sm text-gray-600 mb-1">Formato</p>
                          <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                            {logoAlmacen.contentType.split('/')[1].toUpperCase()}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-gray-50">
                          <p className="text-sm text-gray-600 mb-1">Color de Fondo</p>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-6 h-6 rounded border border-gray-300"
                              style={{ backgroundColor: logoAlmacen.colorFondo }}
                            />
                            <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                              {logoAlmacen.colorFondo}
                            </p>
                          </div>
                        </div>
                        <div className="p-4 rounded-lg bg-gray-50 md:col-span-2">
                          <p className="text-sm text-gray-600 mb-1">Fecha de Creación</p>
                          <p className="text-base font-semibold" style={{ color: '#11194F' }}>
                            {formatFecha(logoAlmacen.fechaCreacion.split('T')[0])}
                          </p>
                        </div>
                      </div>

                      {/* Botones de Acción */}
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => setIsChangingLogo(true)}
                          className="px-6 py-3 bg-[#43BFFF] text-white rounded-lg font-semibold hover:bg-[#3AABEB] transition-colors"
                        >
                          Cambiar Logo
                        </button>
                        <button
                          onClick={() => setShowEliminarLogoModal(true)}
                          className="px-6 py-3 border-2 border-red-300 text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-colors"
                        >
                          Eliminar Logo
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Sin logo (status 204) o cambiando logo - Mostrar interfaz de subida
                <div className="space-y-6">
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#11194F' }}>
                    {isChangingLogo ? 'Cambiar Logo' : 'Subir Logo'}
                  </h3>

                  {/* Especificaciones */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Especificaciones requeridas:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Extensión: PNG o JPG</li>
                      <li>• Dimensión mínima: 128×128 px</li>
                      <li>• Dimensión máxima: 512×512 px</li>
                      <li>• Tamaño máximo: 200 KB</li>
                    </ul>
                  </div>

                  {/* Zona de Drag & Drop */}
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      isDragging
                        ? 'border-[#43BFFF] bg-blue-50'
                        : 'border-gray-300 hover:border-[#43BFFF] hover:bg-gray-50'
                    }`}
                  >
                    <MdCloudUpload
                      className="text-6xl mx-auto mb-4"
                      style={{ color: isDragging ? '#43BFFF' : '#9CA3AF' }}
                    />
                    <p className="text-lg font-semibold mb-2" style={{ color: '#11194F' }}>
                      {isDragging ? 'Suelta la imagen aquí' : 'Arrastra tu logo aquí'}
                    </p>
                    <p className="text-sm text-gray-600 mb-4">o</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-6 py-3 bg-[#43BFFF] text-white rounded-lg font-semibold hover:bg-[#3AABEB] transition-colors"
                    >
                      Seleccionar Archivo
                    </button>
                  </div>

                  {/* Error de Validación */}
                  {validationError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 font-semibold">{validationError}</p>
                    </div>
                  )}

                  {/* Vista Previa */}
                  {previewUrl && selectedFile && (
                    <div className="space-y-4">
                      <h4 className="font-semibold" style={{ color: '#11194F' }}>
                        Vista Previa
                      </h4>
                      
                      {/* Grid con 3 vistas previas */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* 1. Vista Principal (Logo circular) */}
                        <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                          <p className="text-xs font-semibold text-gray-600 mb-3 text-center">
                            Vista Principal
                          </p>
                          <div
                            className="relative rounded-full shadow-lg flex items-center justify-center"
                            style={{
                              backgroundColor: colorFondo,
                              border: '3px solid rgb(17, 25, 79)',
                              width: '120px',
                              height: '120px',
                              padding: '16px',
                            }}
                          >
                            <img
                              src={previewUrl}
                              alt="Vista previa principal"
                              className="object-contain w-full h-full"
                              style={{
                                borderRadius: '15px'
                              }}
                            />
                          </div>
                        </div>

                        {/* 2. Vista Sidebar */}
                        <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg border-2 border-gray-200 relative overflow-hidden">
                          <p className="text-xs font-semibold text-gray-600 mb-3 text-center">
                            En el Menú
                          </p>
                          {/* Simulación del Sidebar completo */}
                          <div
                            className="shadow-lg relative overflow-hidden"
                            style={{
                              backgroundColor: '#11194F',
                              width: '80px',
                              height: '200px',
                            }}
                          >
                            {/* Header del sidebar con logo */}
                            <div className="flex items-center justify-center p-3 border-b border-white border-opacity-10">
                              <div
                                className="rounded-full flex items-center justify-center overflow-hidden"
                                style={{
                                  backgroundColor: colorFondo,
                                  width: '44px',
                                  height: '44px',
                                  padding: '8px',
                                }}
                              >
                                <img
                                  src={previewUrl}
                                  alt="Vista previa sidebar"
                                  className="object-contain w-full h-full"
                                />
                              </div>
                            </div>
                            {/* Elementos de menú simulados */}
                            <div className="p-3 justify-center space-y-2">
                              <div className="flex items-center justify-center p-2 rounded bg-opacity-10">
                                <div className="w-8 h-8 bg-white bg-opacity-50 rounded"></div>
                              </div>
                              <div className="flex items-center justify-center p-2 rounded">
                                <div className="w-8 h-8 bg-white bg-opacity-20 rounded"></div>
                              </div>
                            </div>
                            {/* Difuminado inferior para simular más contenido */}
                            <div
                              style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '100px',
                                background: 'linear-gradient(180deg, rgba(17, 25, 79, 0) 0%, rgba(17, 25, 79, 0.4) 30%, rgba(17, 25, 79, 0.9) 70%, rgba(17, 25, 79, 1) 100%)',
                                pointerEvents: 'none',
                                zIndex: 10,
                              }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-500 mt-2 text-center">
                            Aparecerá en la esquina superior izquierda del menú
                          </p>
                          {/* Difuminado inferior del contenedor 
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '120px',
                              background: 'linear-gradient(180deg, rgba(249, 250, 251, 0) 0%, rgba(249, 250, 251, 0.5) 30%, rgba(249, 250, 251, 0.85) 60%, rgba(249, 250, 251, 1) 100%)',
                              pointerEvents: 'none',
                              zIndex: 20,
                            }}
                          />
                          */}
                        </div>

                        {/* 3. Vista Boleta/Ticket */}
                        <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                          <p className="text-xs font-semibold text-gray-600 mb-3 text-center">
                            En la Boleta
                          </p>
                          <div
                            className="bg-white border border-gray-300 rounded"
                            style={{
                              width: '180px',
                              padding: '10px',
                              fontFamily: 'monospace',
                              fontSize: '8px',
                            }}
                          >
                            {/* Simulación de boleta térmica */}
                            <div className="text-center">
                              {/* Logo sin fondo de color */}
                              <div className="flex items-center justify-center mx-auto mb-1">
                                <img
                                  src={previewUrl}
                                  alt="Vista previa boleta"
                                  className="object-contain"
                                  style={{
                                    width: '35px',
                                    height: '35px',
                                  }}
                                />
                              </div>
                              <div className="border-t border-dashed border-gray-400 my-1.5"></div>
                              <div className="font-bold text-[8px] mb-0.5">BOLETA N° #######</div>
                              <div className="text-[7px]">RUT: XX.XXX.XXX-X</div>
                              <div className="text-[7px]">Tu nombre de almacén</div>
                              <div className="text-[7px]">Giro Comercial</div>
                              <div className="text-[7px] mb-1">Tu dirección</div>
                              <div className="text-[7px] mb-1">dd/MM/yyyy - hh:mm</div>
                              <div className="border-t border-dashed border-gray-400 my-1.5"></div>
                              <div className="text-left mb-1">
                                <div className="grid grid-cols-12 gap-0.5 text-[7px] font-bold mb-0.5">
                                  <div className="col-span-2">Cant.</div>
                                  <div className="col-span-6">Producto</div>
                                  <div className="col-span-4 text-right">Total</div>
                                </div>
                                <div className="grid grid-cols-12 gap-0.5 text-[7px]">
                                  <div className="col-span-2">5</div>
                                  <div className="col-span-6">Producto 1</div>
                                  <div className="col-span-4 text-right">$##.###</div>
                                </div>
                                <div className="grid grid-cols-12 gap-0.5 text-[7px]">
                                  <div className="col-span-2">1</div>
                                  <div className="col-span-6">Producto 2</div>
                                  <div className="col-span-4 text-right">$##.###</div>
                                </div>
                              </div>
                              <div className="border-t border-dashed border-gray-400 my-1.5"></div>
                              <div className="flex justify-between text-[8px] font-bold">
                                <span>TOTAL:</span>
                                <span>$##.###</span>
                              </div>
                              <div className="border-t border-dashed border-gray-400 my-1.5"></div>
                              <div className="text-[7px] mt-1">Almacenes Digitales</div>
                              <div className="text-[7px] font-bold">¡Gracias por tu compra!</div>
                            </div>
                          </div>
                          <p className="text-[10px] text-gray-500 mt-2 text-center">
                            Aparecerá al inicio de cada ticket impreso
                          </p>
                        </div>
                      </div>

                      {/* Selector de Color de Fondo */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-4">
                          <label className="font-semibold" style={{ color: '#11194F' }}>
                            Color de Fondo:
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={colorFondo}
                              onChange={(e) => setColorFondo(e.target.value.toUpperCase())}
                              className="w-12 h-12 rounded border-2 border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={colorFondo}
                              onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                if (/^#[0-9A-F]{0,6}$/.test(value)) {
                                  setColorFondo(value);
                                }
                              }}
                              className="px-3 py-2 border-2 border-gray-300 rounded-lg font-mono text-sm w-24"
                              placeholder="#FFFFFF"
                              maxLength={7}
                            />
                          </div>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-sm text-amber-800">
                            <span className="font-semibold">💡 Recomendación:</span> El color de fondo se verá mejor si tu logo es un PNG sin fondo. Si tu imagen ya tiene fondo, te recomendamos dejar el color en blanco (#FFFFFF).
                          </p>
                        </div>
                      </div>

                      {/* Botones de Acción */}
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowSubirLogoModal(true)}
                          className="px-6 py-3 bg-[#43BFFF] text-white rounded-lg font-semibold hover:bg-[#3AABEB] transition-colors"
                        >
                          Guardar Logo
                        </button>
                        <button
                          onClick={resetUploadState}
                          className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Categorías del almacén */}
          {activeTab === 'categorias' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                  <MdCategory className="text-3xl text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                    Categorías del negocio
                  </h2>
                  <p className="text-sm text-gray-600">
                    {hasPremiumAccess ? 'Organiza tus productos con categorías propias' : 'Función exclusiva Premium'}
                  </p>
                </div>
              </div>

              {!hasPremiumAccess ? (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 mb-6">
                    <FaCrown className="text-5xl text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#11194F' }}>Función Premium</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Las categorías personalizadas están disponibles solo para usuarios con suscripción Premium.
                  </p>
                  <button
                    onClick={() => goToPremium()}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  >
                    Ver Planes
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-base font-semibold mb-3" style={{ color: '#11194F' }}>Nueva categoría</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={nuevaCategoria}
                        onChange={(e) => setNuevaCategoria(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCrearCategoria()}
                        placeholder="Ej: Ofertas de la semana"
                        maxLength={50}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#43BFFF' } as React.CSSProperties}
                      />
                      <button
                        onClick={handleCrearCategoria}
                        disabled={!nuevaCategoria.trim() || createCategoria.isPending}
                        className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-40"
                        style={{ backgroundColor: '#43BFFF' }}
                      >
                        <MdAdd className="text-lg" />
                        Agregar
                      </button>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-base font-semibold mb-3" style={{ color: '#11194F' }}>
                      Mis categorías {categorias && `(${categorias.length})`}
                    </h3>
                    {isLoadingCategorias ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                      </div>
                    ) : !categorias || categorias.length === 0 ? (
                      <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
                        <MdCategory className="text-4xl mx-auto mb-2 text-gray-300" />
                        <p className="text-gray-500 text-sm">Aún no tienes categorías. Crea la primera.</p>
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {categoriasOrdenadas.map((cat, index) => (
                          <li key={cat.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
                            {categoriaEditando?.id === cat.id ? (
                              <>
                                <input
                                  type="text"
                                  value={nombreEditando}
                                  onChange={(e) => setNombreEditando(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleGuardarEdicion();
                                    if (e.key === 'Escape') setCategoriaEditando(null);
                                  }}
                                  maxLength={50}
                                  autoFocus
                                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                                  style={{ '--tw-ring-color': '#43BFFF' } as React.CSSProperties}
                                />
                                <button
                                  onClick={handleGuardarEdicion}
                                  disabled={!nombreEditando.trim() || updateCategoria.isPending}
                                  className="p-1.5 rounded text-white transition-opacity disabled:opacity-40"
                                  style={{ backgroundColor: '#43BFFF' }}
                                  title="Guardar"
                                >
                                  <MdCheck className="text-base" />
                                </button>
                                <button
                                  onClick={() => setCategoriaEditando(null)}
                                  className="p-1.5 rounded text-gray-500 hover:bg-gray-200 transition-colors"
                                  title="Cancelar"
                                >
                                  <MdClose className="text-base" />
                                </button>
                              </>
                            ) : (
                              <>
                                <div className="flex flex-col gap-0.5">
                                  <button
                                    onClick={() => moverCategoria(index, 'arriba')}
                                    disabled={index === 0}
                                    className="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                                    title="Subir"
                                  >
                                    <MdArrowUpward className="text-sm" />
                                  </button>
                                  <button
                                    onClick={() => moverCategoria(index, 'abajo')}
                                    disabled={index === categoriasOrdenadas.length - 1}
                                    className="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                                    title="Bajar"
                                  >
                                    <MdArrowDownward className="text-sm" />
                                  </button>
                                </div>
                                <span className="flex-1 text-sm font-medium" style={{ color: '#11194F' }}>{cat.nombre}</span>
                                <button
                                  onClick={() => { setCategoriaEditando(cat); setNombreEditando(cat.nombre); }}
                                  className="p-1.5 rounded text-gray-500 hover:bg-gray-200 transition-colors"
                                  title="Editar"
                                >
                                  <MdEdit className="text-base" />
                                </button>
                                <button
                                  onClick={() => setCategoriaAEliminar(cat)}
                                  className="p-1.5 rounded text-red-400 hover:bg-red-50 transition-colors"
                                  title="Eliminar"
                                >
                                  <MdDelete className="text-base" />
                                </button>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmacionModal
        isOpen={!!categoriaAEliminar}
        onClose={() => setCategoriaAEliminar(null)}
        onConfirm={handleConfirmarEliminar}
        title="Eliminar categoría"
        message={`¿Estás seguro de eliminar la categoría "${categoriaAEliminar?.nombre}"? Los productos que la tenían asignada quedarán sin categoría propia.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={deleteCategoria.isPending}
        variant="danger"
      />

      

      {/* Modales */}
      <CrearSucursalModal
        isOpen={isCrearSucursalModalOpen}
        onClose={() => setIsCrearSucursalModalOpen(false)}
      />

      <EditarSucursalModal
        isOpen={!!sucursalToEdit}
        onClose={() => setSucursalToEdit(null)}
        sucursal={sucursalToEdit}
      />

      <CrearUsuarioModal
        isOpen={isCrearUsuarioModalOpen}
        onClose={() => setIsCrearUsuarioModalOpen(false)}
      />

      <EditarUsuarioModal
        isOpen={!!usuarioToEdit}
        onClose={() => setUsuarioToEdit(null)}
        usuario={usuarioToEdit}
      />

      <ConfirmacionModal
        isOpen={!!sucursalToDelete}
        onClose={() => setSucursalToDelete(null)}
        onConfirm={handleEliminarSucursal}
        title="Eliminar Sucursal"
        message={`¿Está seguro de eliminar la sucursal "${sucursalToDelete?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={eliminarSucursalMutation.isPending}
        variant="danger"
      />

      <ConfirmacionModal
        isOpen={!!usuarioToDelete}
        onClose={() => setUsuarioToDelete(null)}
        onConfirm={handleEliminarUsuario}
        title="Eliminar Usuario"
        message={`¿Está seguro de eliminar al usuario "${usuarioToDelete?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={eliminarUsuarioMutation.isPending}
        variant="danger"
      />

      <ConfirmacionModal
        isOpen={showEliminarLogoModal}
        onClose={() => setShowEliminarLogoModal(false)}
        onConfirm={handleEliminarLogo}
        title="Eliminar Logo"
        message="¿Está seguro de eliminar el logo de su negocio? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={eliminarLogoMutation.isPending}
        variant="danger"
      />

      <ConfirmacionModal
        isOpen={showSubirLogoModal}
        onClose={() => setShowSubirLogoModal(false)}
        onConfirm={handleSubirLogo}
        title="Confirmar Subida"
        message="¿Está seguro de que desea subir esta imagen como logo de su negocio?"
        confirmText="Subir Logo"
        cancelText="Cancelar"
        isLoading={subirLogoMutation.isPending}
        variant="info"
      />
    </div>
  );
};

